import React from 'react';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import * as Yup from 'yup';
import { Form, FormikProvider, useFormik } from 'formik';
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from '@mui/lab';
import CheckIcon from "@mui/icons-material/Check";
import FormBuilder from '../form-builder';
import { INVITE_USER_FORM_INPUTS } from '../../builders/customers-builders';
import { CustomerService } from '../../services/customer.service';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

interface Props {
    subscriptionId: string,
    visible: boolean,
    setVisible: (visible: boolean) => void,
};

export const InviteUserToSubscriptionModal: React.FC<Props> = ({
    visible,
    subscriptionId,
    setVisible,
}) => {
    const { loading } = useSelector((state: RootState) => state.customer);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const validationSchema = Yup.object().shape({
        email: Yup
            .string()
            .required('Please provide the user email'),
    });

    const formik = useFormik<{ email: string }>({
        initialValues: {
            email: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            await CustomerService.inviteUserToSubscription(values.email, subscriptionId);
            formik.resetForm();
            onClose();
        },
    });
      
    const { handleSubmit, errors, touched, getFieldProps } = formik;
    
    const onClose = () => setVisible(false);

    return (
        <Dialog
            open={visible}
            maxWidth={'xl'}
            scroll={'paper'}
            fullWidth={isSmallScreen}
        >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction={'row'} justifyContent={'space-between'}>
                    <Typography sx={{ ml: 3, mb: 2, mt: 2 }} fontSize={18} fontWeight={400} color={'text.default'}>
                        Invite a new user
                    </Typography>
                    <Tooltip title="Close">
                        <IconButton
                            onClick={onClose}
                            component="span"
                            sx={{ mr: 2, height: 30, width: 30, alignSelf: 'center' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
            <FormikProvider value={formik}>
                <Form noValidate onSubmit={handleSubmit}>
                    <DialogContent style={{ minWidth: 400 }} dividers>
                        <FormBuilder
                            errors={errors}
                            getFieldProps={getFieldProps}
                            touched={touched}
                            inputs={[...INVITE_USER_FORM_INPUTS]}
                        />
                    </DialogContent>
                    <DialogActions>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            loading={loading.inviteUserToSubscription}
                            size='medium'
                            fullWidth={isSmallScreen}
                        >
                            Submit invitation
                        </LoadingButton>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </Dialog>
    )
}